/*
  BONE WATER — EVENT LAYER
  assets/bw-analytics.js

  One function, window.bwTrack(name, payload), that sections call to report
  what a visitor did. Everything else in here is plumbing.

  WHY A SHIM AND NOT DIRECT CALLS

  A section that calls Shopify.analytics.publish directly is betting that the
  destination never changes. It always does: a store picks up GTM, or swaps
  GA4 for a server-side pixel, or adds Meta. Each of those is a rewrite across
  every section that reports anything. Here it is one file, and the sections
  keep saying what happened rather than where to send it.

  WHERE EVENTS GO

  Every consumer that is present receives every event. None of them are
  required — with nothing installed this is a few microseconds of no-op, which
  is the correct behaviour for a theme that ships before its analytics stack.

    Shopify.analytics.publish   the canonical path. Web Pixels subscribe to
                                these, so a custom pixel in the admin can
                                forward to any destination without a theme
                                edit. This is the one to wire up first.
    dataLayer                   GTM. Pushed as {event: name, ...payload} so
                                the name works as a GTM trigger directly.
    gtag                        GA4 installed straight into the theme.

  AS INSTALLED, ONLY THE FIRST ONE RUNS

  Bone Water's GA4, Meta and TikTok pixels come from the Shopify channel
  apps, and those install sandboxed Web Pixels rather than page scripts.
  Nothing puts gtag or dataLayer on the storefront window, so those two
  branches never find a consumer and never will while the setup stays as it
  is. They are not dead code to be tidied away — they are what makes adding
  GTM later a config change instead of a rewrite, which is the entire reason
  this file exists. Leave them.

  The live path is Shopify.analytics.publish, picked up by the custom pixel
  in Settings → Customer events. Its source is kept at docs/bw-custom-pixel.js.

  THE QUEUE

  Shopify.analytics is injected by content_for_header but is not guaranteed to
  have finished initialising when a section's DOMContentLoaded handler runs —
  and the most valuable event on the page, the section impression, fires early
  by definition. Events therefore buffer until a consumer appears, then flush
  in order. Without this the first events of every session are silently lost,
  which is worse than having no analytics at all: the funnel looks like people
  arrive already halfway down it.

  The queue is capped. If nothing ever shows up to drain it — a store with no
  analytics at all — an unbounded array would grow for the life of the page.

  NAMING

  All events are prefixed bw_ and named subject_verb in past tense. The prefix
  keeps them clear of Shopify's own standard events (product_viewed,
  checkout_started) so a GA4 property can filter on it, and so nothing here
  can ever collide with a name Shopify adds later.
*/

(function () {
  'use strict';

  var QUEUE = [];
  var QUEUE_MAX = 50;
  var flushed = false;

  /* Debug mode. ?bw_debug=1 on any URL prints every event to the console —
     the fastest way to confirm wiring without opening a analytics dashboard
     and waiting on its processing delay. Deliberately opt-in per URL rather
     than a build flag, so it works on the live store too. */
  var debug = false;
  try {
    debug = window.location.search.indexOf('bw_debug=1') > -1;
  } catch (e) {}

  function consumers() {
    var out = [];

    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      out.push(function (name, payload) {
        window.Shopify.analytics.publish(name, payload);
      });
    }

    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      out.push(function (name, payload) {
        var obj = { event: name };
        for (var k in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, k)) obj[k] = payload[k];
        }
        window.dataLayer.push(obj);
      });
    }

    if (typeof window.gtag === 'function') {
      out.push(function (name, payload) {
        window.gtag('event', name, payload);
      });
    }

    return out;
  }

  function deliver(sinks, name, payload) {
    for (var i = 0; i < sinks.length; i++) {
      /* One broken destination must not take the others down with it, and an
         analytics failure must never break the page it is measuring. */
      try {
        sinks[i](name, payload);
      } catch (e) {
        if (debug) console.warn('[bw] sink failed for ' + name, e);
      }
    }
  }

  function track(name, payload) {
    if (!name) return;
    payload = payload || {};

    if (debug) console.log('[bw] ' + name, payload);

    var sinks = consumers();

    if (!sinks.length) {
      if (QUEUE.length < QUEUE_MAX) QUEUE.push([name, payload]);
      return;
    }

    /* First delivery of the page also drains anything that arrived before a
       consumer existed. Order is preserved: the buffered events happened
       first and a funnel report that reordered them would be wrong. */
    if (!flushed) {
      flushed = true;
      var pending = QUEUE.splice(0, QUEUE.length);
      for (var i = 0; i < pending.length; i++) {
        deliver(sinks, pending[i][0], pending[i][1]);
      }
    }

    deliver(sinks, name, payload);
  }

  window.bwTrack = track;

  /* A late-arriving consumer (a tag manager that loads on idle) would other-
     wise leave the queue stranded. One retry pass on load covers it; beyond
     that, anything still queued is a store with no analytics installed and
     there is nothing useful left to do. */
  window.addEventListener('load', function () {
    if (!flushed && QUEUE.length && consumers().length) track('bw_queue_flushed', {});
  });
})();
